import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ConciergeBell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  REVENUE_CHART_CARD_SHADOW,
  TOP_SERVICES_CARD_BORDER,
  TOP_SERVICES_CARD_ICON_COLOR,
  TOP_SERVICES_CARD_GAP,
  TOP_SERVICES_CARD_PADDING_BOTTOM,
  TOP_SERVICES_CARD_PADDING_TOP,
  TOP_SERVICES_CARD_MIN_HEIGHT,
  TOP_SERVICES_CARD_PADDING_X,
  TOP_SERVICES_CARD_RADIUS,
  TOP_SERVICES_CARD_WIDTH,
  TOP_SERVICES_SERVICE_CELL_WIDTH,
  TOP_SERVICES_TABLE_ROW_HEIGHT,
} from "./constants";
import { METRIC_CARD_ICON_STROKE_WIDTH } from "../shared/constants";
import {
  GuestsUsedCell,
  RateBadge,
  RevenueCell,
  ServiceNameCellContent,
} from "./topServicesTableCells";
import type { TopServiceRow } from "./mockData";

const TABLE_LABEL_CLASS =
  "h-10 border-b border-[#E5E5E5] p-2 text-center align-middle text-[10px] font-medium leading-5 text-[#737373]";

const TABLE_BODY_ROW_STYLE = {
  height: TOP_SERVICES_TABLE_ROW_HEIGHT,
  maxHeight: TOP_SERVICES_TABLE_ROW_HEIGHT,
} as const;

const TABLE_BODY_CELL_STYLE = {
  ...TABLE_BODY_ROW_STYLE,
  boxSizing: "border-box" as const,
};

const TABLE_BODY_CELL_CLASS =
  "box-border overflow-hidden border-b border-[#E5E5E5] p-2 text-center align-middle";

function bodyCellClass(isLastRow: boolean, extra?: string) {
  return cn(TABLE_BODY_CELL_CLASS, isLastRow && "border-b-0", extra);
}

interface TopServicesTableProps {
  rows: TopServiceRow[];
  className?: string;
  onViewAll?: () => void;
  onRowClick?: (row: TopServiceRow) => void;
}

export const TopServicesTable = memo(function TopServicesTable({
  rows,
  className,
  onViewAll,
  onRowClick,
}: TopServicesTableProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white font-sans",
        REVENUE_CHART_CARD_SHADOW,
        className,
      )}
      style={{
        width: TOP_SERVICES_CARD_WIDTH,
        maxWidth: "100%",
        minHeight: TOP_SERVICES_CARD_MIN_HEIGHT,
        borderRadius: TOP_SERVICES_CARD_RADIUS,
        border: `1px solid ${TOP_SERVICES_CARD_BORDER}`,
        paddingTop: TOP_SERVICES_CARD_PADDING_TOP,
        paddingRight: TOP_SERVICES_CARD_PADDING_X,
        paddingBottom: TOP_SERVICES_CARD_PADDING_BOTTOM,
        paddingLeft: TOP_SERVICES_CARD_PADDING_X,
        gap: TOP_SERVICES_CARD_GAP,
      }}
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className="flex shrink-0 items-center justify-center"
            style={{
              color: TOP_SERVICES_CARD_ICON_COLOR,
              width: 13.34,
              height: 13.33,
            }}
          >
            <ConciergeBell
              className="size-[13.34px] shrink-0"
              strokeWidth={METRIC_CARD_ICON_STROKE_WIDTH}
            />
          </span>
          <h3 className="text-sm font-semibold leading-normal text-[#171717]">
            {t("analytics.services.top5Title", "Top 5 Services")}
          </h3>
        </div>
        <Button
          type="button"
          variant="span"
          className="inline-flex h-auto items-center gap-0.5 p-0 text-xs font-medium text-[#171717] hover:bg-transparent"
          onClick={onViewAll}
        >
          {t("analytics.common.viewAll", "View all")}
          <ChevronRight className="size-3.5" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr>
              <th className={cn(TABLE_LABEL_CLASS, "w-9")}>
                {t("analytics.common.rate", "Rate")}
              </th>
              <th
                className={TABLE_LABEL_CLASS}
                style={{
                  width: TOP_SERVICES_SERVICE_CELL_WIDTH,
                  maxWidth: TOP_SERVICES_SERVICE_CELL_WIDTH,
                }}
              >
                {t("analytics.services.table.service", "Service")}
              </th>
              <th className={TABLE_LABEL_CLASS}>
                {t("analytics.services.table.bookings", "Bookings")}
              </th>
              <th className={TABLE_LABEL_CLASS}>
                {t(
                  "analytics.services.table.guestsUsedPercent",
                  "% Guests Used",
                )}
              </th>
              <th className={TABLE_LABEL_CLASS}>
                {t("analytics.services.table.revenue", "Revenue")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isLastRow = index === rows.length - 1;

              return (
                <tr
                  key={row.id}
                  style={TABLE_BODY_ROW_STYLE}
                  className={
                    onRowClick ? "cursor-pointer hover:bg-[#FAFAFA]" : undefined
                  }
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  <td
                    className={bodyCellClass(isLastRow, "w-9")}
                    style={TABLE_BODY_CELL_STYLE}
                  >
                    <div className="flex h-full items-center justify-center">
                      <RateBadge rate={row.rate} />
                    </div>
                  </td>
                  <td
                    className={bodyCellClass(isLastRow)}
                    style={{
                      ...TABLE_BODY_CELL_STYLE,
                      width: TOP_SERVICES_SERVICE_CELL_WIDTH,
                      maxWidth: TOP_SERVICES_SERVICE_CELL_WIDTH,
                      verticalAlign: "middle",
                    }}
                  >
                    <ServiceNameCellContent row={row} />
                  </td>
                  <td
                    className={bodyCellClass(isLastRow, "text-[#171717]")}
                    style={TABLE_BODY_CELL_STYLE}
                  >
                    {row.bookings}
                  </td>
                  <td
                    className={bodyCellClass(isLastRow)}
                    style={TABLE_BODY_CELL_STYLE}
                  >
                    <GuestsUsedCell percent={row.guestsUsedPercent} />
                  </td>
                  <td
                    className={bodyCellClass(isLastRow)}
                    style={TABLE_BODY_CELL_STYLE}
                  >
                    <RevenueCell revenue={row.revenue} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});
